import { useSlotId } from '@react-aria/utils';
import clsx from 'clsx';
import type * as React from 'react';
import { Provider } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';

import { DescriptionContext } from '../Description';
import { Heading, HeadingContext, type HeadingProps } from '../Heading';
import { type IconDefinition, renderIcon } from '../util';
import { useLabelProps } from '../util/labeling';

export type EmptyStateProps = React.ComponentPropsWithoutRef<'div'> & {
    size?: 'small' | 'medium' | 'large';
    illustration?: React.ReactElement;
};

export function EmptyState(props: EmptyStateProps) {
    const { size = 'medium', illustration, className, children, ...rest } = props;

    const headingId = useSlotId();
    const descriptionId = useSlotId();

    const labelProps = useLabelProps({ ...props, headingId, descriptionId });

    return (
        <Provider
            values={[
                [
                    HeadingContext,
                    {
                        level: 3,
                        className:
                            size === 'small' ? 'text-ui-standout mt-2' : 'text-ui-heading-medium',
                        id: headingId,
                    },
                ],
                [DescriptionContext, { id: descriptionId, className: 'text-balance' }],
            ]}
        >
            <div
                {...rest}
                role="status"
                {...labelProps}
                className={clsx(
                    'rounded-md text-center',
                    !illustration && contentStyle,
                    !illustration && sizeStyle[size],
                    className
                )}
            >
                {illustration ? (
                    <div className={clsx(contentStyle, sizeStyle[size])}>
                        <div className="flex w-full items-center justify-center">
                            {illustration}
                        </div>
                        {children}
                    </div>
                ) : (
                    children
                )}
            </div>
        </Provider>
    );
}
const contentStyle =
    'mx-auto flex items-center justify-center max-w-lg flex-col h-full self-center justify-self-center gap-2';
const sizeStyle = {
    small: 'p-6',
    medium: 'p-8',
    large: 'p-8',
};

export type EmptyStateIllustrationProps = React.ComponentPropsWithoutRef<'div'>;
export function EmptyStateIllustration(props: EmptyStateIllustrationProps) {
    return <div {...props} className={clsx('flex shrink-0', props.className)} />;
}

export type EmptyStateIconProps = { children: IconDefinition };

export function EmptyStateIcon(props: EmptyStateIconProps) {
    return renderIcon(props.children, { size: 16, className: 'mb-2 text-muted shrink-0' });
}

export type EmptyStateActionProps = React.ComponentPropsWithoutRef<'div'>;

export function EmptyStateActions(props: EmptyStateActionProps) {
    return (
        <div
            {...props}
            className={clsx(
                'mt-2 flex flex-wrap items-center justify-center gap-4 empty:hidden',
                props.className
            )}
        />
    );
}

export type EmptyStateNotFoundHeadingProps = Omit<HeadingProps, 'children'>;

export function EmptyStateNotFoundHeading(props: EmptyStateNotFoundHeadingProps) {
    return <Heading {...props}>Not found</Heading>;
}

export function EmptyStateAccessDeniedHeader() {
    return (
        <>
            <EmptyStateIcon>
                <Icon.PermissionNone />
            </EmptyStateIcon>
            <Heading>Access denied</Heading>
        </>
    );
}

export function EmptyStateErrorHeader() {
    return (
        <>
            <EmptyStateIcon>
                <Icon.Warning />
            </EmptyStateIcon>
            <Heading>Something went wrong</Heading>
        </>
    );
}
